import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { useT } from '@transifex/react';
import { Button, Divider, Typography } from '@raisin/components-lib';
import ShowMoreIcon from '@raisin/components-lib/Icons/AddBox';
import ShowLessIcon from '@raisin/components-lib/Icons/IndeterminateCheckBox';
import { trackEvent } from '@raisin/events-tracking';
import { BANK_EVENTS, COMMON_EVENT_PROPERTIES } from 'eventsTracking';
import { FormattedBankAdditionalDetails } from 'utils/bankPageUtils/bankAdditionalDetails';
import messages from '../messages';

const TableRow = ({ field, value }) => (
  <>
    <Box display="flex" my={3} width={1}>
      <Typography variant="subtitle1" sx={{ flex: 1 }}>
        {field}
      </Typography>
      <Typography sx={{ flex: 1 }}>{value}</Typography>
    </Box>
    <Divider />
  </>
);

const EVENT = {
  ...COMMON_EVENT_PROPERTIES,
  eventName: BANK_EVENTS.ADDITIONAL_DETAILS_EXPANDED,
  context: 'category-management-banks',
  action: 'expanded',
  object: 'bank-additional-details',
} as const;

interface BankAdditionalDetailsProps {
  data: FormattedBankAdditionalDetails;
  previewCount?: number;
}

const BankAdditionalDetails: React.FC<BankAdditionalDetailsProps> = ({
  data,
  previewCount = 5,
}) => {
  const t = useT();
  const previewFields = useMemo(() => data?.tableFields?.slice(0, previewCount), []);
  const collapsibleFields = useMemo(() => data?.tableFields?.slice(previewCount), []);

  const [isExpanded, setExpanded] = useState(false);
  const handleClick = () => {
    if (!isExpanded) trackEvent(EVENT);

    setExpanded(!isExpanded);
  };

  return (
    <>
      <Divider />
      {previewFields?.map((row) => (
        <TableRow field={row?.title} value={row?.value} key={row?.id} />
      ))}
      <Collapse in={isExpanded}>
        {isExpanded &&
          collapsibleFields?.map((row) => (
            <TableRow field={row?.title} value={row?.value} key={row?.id} />
          ))}
      </Collapse>
      <Button
        onClick={handleClick}
        mode="primaryText"
        startIcon={isExpanded ? <ShowLessIcon /> : <ShowMoreIcon />}
        sx={{ my: 3 }}
      >
        {isExpanded ? t(messages.showLess) : t(messages.showMore)}
      </Button>
      <Typography variant="body2">* {data?.footnote}</Typography>
    </>
  );
};

export default BankAdditionalDetails;
